import React, { useEffect, useState } from "react";
import { API_URL } from "../../../constants/appConfig";
import { useNavigate } from "react-router-dom";
import myToast from "../../../components/toast";
import {
  Card,
  CardContent,
  Typography,
  Button,
  IconButton,
  Tooltip,
  TextField,
  Box,
  Dialog,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  tableCellClasses,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Avatar,
  Grid,
} from "@mui/material";
import {
  Edit as PencilIcon,
  Visibility as EyeIcon,
  Delete as TrashIcon,
  Search as MagnifyingGlassIcon,
  CheckCircle as ActiveIcon,
  Cancel as InactiveIcon,
  Close as CloseIcon,
} from "@mui/icons-material";
import { styled } from "@mui/material/styles";
import { InputAdornment } from "@mui/material";
import {
  NavigateBefore as NavigateBeforeIcon,
  NavigateNext as NavigateNextIcon,
} from "@mui/icons-material";
import { useTheme } from "@mui/material/styles";
import { useMediaQuery } from "@mui/material";

// TableCell için özel stil bileşeni
const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: theme.palette.primary.lighter,
    color: theme.palette.primary.main,
    fontWeight: 600,
    fontSize: "0.875rem",
    padding: "16px",
    whiteSpace: "nowrap",
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: "0.875rem",
    padding: "16px",
  },
}));

// TableRow için özel stil bileşeni
const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:nth-of-type(odd)": {
    backgroundColor: theme.palette.action.hover,
  },
  "&:hover": {
    backgroundColor: theme.palette.primary.lighter,
    cursor: "pointer",
    "& .MuiIconButton-root": {
      opacity: 1,
    },
  },
  "& .MuiIconButton-root": {
    opacity: 0.5,
    transition: "all 0.2s",
  },
  // son row'da border olmasın
  "&:last-child td, &:last-child th": {
    border: 0,
  },
}));

// Status chip bileşeni
const StatusChip = styled(Box)(({ theme, status }) => ({
  padding: "6px 12px",
  borderRadius: "16px",
  display: "inline-flex",
  alignItems: "center",
  gap: "6px",
  fontSize: "0.75rem",
  fontWeight: 600,
  backgroundColor: status
    ? theme.palette.success.lighter
    : theme.palette.error.lighter,
  color: status ? theme.palette.success.main : theme.palette.error.main,
}));

// Mobil için kart bileşeni
const MobileProductCard = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  borderRadius: theme.shape.borderRadius,
  border: `1px solid ${theme.palette.divider}`,
  backgroundColor: theme.palette.background.paper,
  marginBottom: theme.spacing(2),
  width: "100%",
  boxSizing: "border-box",
  maxWidth: "100%",
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(1.5),
    borderRadius: 0,
    borderLeft: 0,
    borderRight: 0,
  },
  "&:hover": {
    backgroundColor: theme.palette.primary.lighter,
    transform: "translateY(-2px)",
    transition: "all 0.3s",
  },
}));

const GoldProducts = () => {
  const [products, setProducts] = useState([]);
  const [totalPages, setTotalPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(0);
  const [loading, setLoading] = useState(false);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [openHideModal, setOpenHideModal] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState({});
  const [searchText, setSearchText] = useState("");
  const [searchValue, setSearchValue] = useState("");
  const [openImageModal, setOpenImageModal] = useState(false);
  const [selectedImage, setSelectedImage] = useState({ url: "", name: "" });
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [sortBy, setSortBy] = useState("createdAt");
  const [sortOrder, setSortOrder] = useState("desc");
  const [totalItems, setTotalItems] = useState(0);

  const handleOpenDeleteModal = (product) => {
    setOpenDeleteModal(!openDeleteModal);
    setSelectedProduct(product);
  };
  const handleOpenHideModal = (product) => {
    setSelectedProduct(product);
    setOpenHideModal(!openHideModal);
  };
  const handleEditProduct = (product) => {
    navigate("/editproduct?productId=" + product.productId);
  };

  const handleYesDeleteProduct = () => {
    const API =
      API_URL +
      "/product/deleteProductById?productId=" +
      selectedProduct.productId;
    fetch(API, {
      method: "DELETE",
    }).then((response) => {
      if (response.ok) {
        myToast({
          status: "success",
          message: "Ürün başarıyla silindi",
        });
        fetchProducts();
        handleOpenDeleteModal();
      } else {
        myToast({
          status: "error",
          message: "Ürün silinirken bir hata oluştu",
        });
      }
    });
  };

  const handleYesHideProduct = () => {
    const API =
      API_URL +
      "/product/hideProductById?productId=" +
      selectedProduct.productId;
    fetch(API, {
      method: "DELETE",
    }).then((response) => {
      if (response.ok) {
        myToast({
          status: "success",
          message: "Ürün başarıyla gizlendi",
        });
        fetchProducts();
        handleOpenHideModal();
      } else {
        myToast({
          status: "error",
          message: "Ürün gizlenirken bir hata oluştu",
        });
      }
    });
  };

  const navigate = useNavigate();

  // İlk yüklemede çalışacak
  useEffect(() => {
    fetchProducts();
  }, []);

  // Ana fetch fonksiyonu
  const fetchProducts = async () => {
    setLoading(true);
    try {
      let API;
      if (searchText && searchText.trim().length >= 3) {
        const encodedSearch = encodeURIComponent(searchText.trim());
        API = `${API_URL}/product/getAllProductIgnore?productGold=true&page=${currentPage}&size=${rowsPerPage}&param=${encodedSearch}&sort=${sortBy},${sortOrder}`;
      } else {
        API = `${API_URL}/product/findByGoldAndUsd?productGold=true&page=${currentPage}&size=${rowsPerPage}&sort=${sortBy},${sortOrder}`;
      }

      const response = await fetch(API);
      const data = await response.json();
      setProducts(data.content);
      setTotalPages(data.totalPages);
      setTotalItems(data.totalElements);
    } catch (error) {
      console.error("Veri çekme hatası:", error);
      myToast({ status: "error", message: "Ürünler yüklenirken hata oluştu" });
    } finally {
      setLoading(false);
    }
  };

  // Sayfa, sıralama veya satır sayısı değiştiğinde verileri yeniden yükle
  useEffect(() => {
    fetchProducts();
  }, [currentPage, rowsPerPage, sortBy, sortOrder, searchText]);

  // Arama fonksiyonu
  const handleSearch = () => {
    if (searchValue.trim().length > 0 && searchValue.trim().length < 3) {
      myToast({
        status: "warning",
        message: "En az 3 karakter girmelisiniz",
      });
      return;
    }

    setCurrentPage(0); // Aramada ilk sayfaya dön
    setSearchText(searchValue); // Aktif aramayı güncelle
  };

  // Sayfa değiştirme
  const handlePageChange = (newPage) => {
    if (newPage >= 0 && newPage < totalPages) {
      setCurrentPage(newPage);
    }
  };

  // Arama alanını temizle
  const handleClearSearch = () => {
    setSearchValue("");
    setSearchText("");
    setCurrentPage(0);
  };

  // Enter tuşuna basıldığında arama yap
  const handleKeyPress = (event) => {
    if (event.key === "Enter") {
      handleSearch();
    }
  };

  // Satır sayısı değiştiğinde
  const handleChangeRowsPerPage = (event) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    setCurrentPage(0); // Satır sayısı değiştiğinde ilk sayfaya dön
  };

  // Sıralama değiştiğinde
  const handleSortChange = (event) => {
    const [newSortBy, newSortOrder] = event.target.value.split(",");
    setSortBy(newSortBy);
    setSortOrder(newSortOrder);
    setCurrentPage(0); // Sıralama değiştiğinde ilk sayfaya dön
  };

  const TABLE_HEAD = [
    { id: "image", label: "RESİM", width: "150px" },
    { id: "name", label: "İSİM", width: "300px" },
    { id: "milyem", label: "MİLYEM", width: "150px" },
    { id: "gram", label: "GRAM", width: "150px" },
    { id: "ayar", label: "AYAR", width: "150px" },
    { id: "durum", label: "DURUM", width: "150px" },
    { id: "fiyat", label: "FİYAT", width: "150px" },
    { id: "actions", label: "", width: "200px" },
  ];

  // Resim modal'ını açma fonksiyonu
  const handleOpenImageModal = (imageUrl, productName) => {
    setSelectedImage({
      url: API_URL + "/" + imageUrl,
      name: productName,
    });
    setOpenImageModal(true);
  };

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  return (
    <Card
      sx={{
        width: "100%",
        boxShadow: { xs: 0, sm: 3 },
        borderRadius: { xs: 0, sm: 2 },
        overflow: "hidden",
      }}
    >
      <CardContent
        sx={{
          p: { xs: 0, sm: 3 },
          "&:last-child": { pb: { xs: 0, sm: 3 } },
        }}
      >
        {/* Üst kısım - Arama, Sıralama ve Ekleme */}
        <div className="flex flex-wrap gap-4 mb-4">
          {/* Arama Alanı */}
          <TextField
            placeholder="Ürün Ara..."
            size="small"
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
            onKeyPress={handleKeyPress}
            sx={{
              minWidth: { xs: "100%", sm: 50 },
              flex: { xs: "1 1 100%", sm: "1 1 auto" },
              mb: { xs: 2, sm: 0 },
            }}
            helperText={
              searchValue.trim().length > 0 && searchValue.trim().length < 3
                ? "En az 3 karakter giriniz"
                : " "
            }
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <MagnifyingGlassIcon color="action" />
                </InputAdornment>
              ),
              endAdornment: searchValue && (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={handleClearSearch}>
                    <CloseIcon />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          {/* Arama Butonu */}
          <Button
            variant="contained"
            onClick={handleSearch}
            disabled={
              searchValue.trim().length > 0 && searchValue.trim().length < 3
            }
            startIcon={<MagnifyingGlassIcon />}
            sx={{
              minWidth: { xs: "100%", sm: 50 },
              height: 40,
              fontSize: "0.875rem",
              textTransform: "none",
              boxShadow: 1,
              flex: { xs: "1 1 100%", sm: "0 0 auto" },
              mb: { xs: 2, sm: 0 },
              "&:hover": {
                boxShadow: 2,
              },
              "& .MuiButton-startIcon": {
                marginRight: 1,
                "& .MuiSvgIcon-root": {
                  fontSize: "1.25rem",
                },
              },
            }}
          >
            Ara
          </Button>

          {/* Sıralama Seçeneği */}
          <TextField
            select
            label="Sıralama"
            value={`${sortBy},${sortOrder}`}
            onChange={handleSortChange}
            size="small"
            sx={{
              minWidth: { xs: "100%", sm: 100 },
              flex: { xs: "1 1 100%", sm: "0 0 auto" },
              mb: { xs: 2, sm: 0 },
            }}
            SelectProps={{
              native: true,
            }}
          >
            <option value="createdAt,desc">En Yeni</option>
            <option value="createdAt,asc">En Eski</option>
          </TextField>

          {/* Sayfa Başına Gösterim */}
          <TextField
            select
            label="Sayfa Başına"
            value={rowsPerPage}
            onChange={handleChangeRowsPerPage}
            size="small"
            sx={{
              minWidth: { xs: "100%", sm: 100 },
              flex: { xs: "1 1 100%", sm: "0 0 auto" },
              mb: { xs: 2, sm: 0 },
            }}
            SelectProps={{
              native: true,
            }}
          >
            <option value={10}>10</option>
            <option value={25}>25</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
          </TextField>

          {/* Ürün Ekleme Butonu */}
          <Button
            onClick={() => navigate("/addproduct")}
            variant="contained"
            color="primary"
            startIcon={<PencilIcon />}
            sx={{
              minWidth: { xs: "100%", sm: 120 },
              height: 40,
              fontSize: "0.875rem",
              textTransform: "none",
              boxShadow: 1,
              flex: { xs: "1 1 100%", sm: "0 0 auto" },
              "&:hover": {
                boxShadow: 2,
              },
              "& .MuiButton-startIcon": {
                marginRight: 1,
                "& .MuiSvgIcon-root": {
                  fontSize: "1.25rem",
                },
              },
            }}
          >
            Ürün ekle
          </Button>
        </div>

        {/* Tablo/Liste Container */}
        <Box
          sx={{
            width: "100%",
            overflow: "hidden",
            ...(isMobile
              ? {
                  px: 0,
                  mx: 0,
                }
              : {
                  border: 1,
                  borderColor: "divider",
                  borderRadius: 1,
                }),
          }}
        >
          {loading ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "400px",
                minWidth: isMobile ? "100%" : "1400px",
              }}
            >
              <CircularProgress />
            </Box>
          ) : isMobile ? (
            // Mobil Görünüm
            <Box sx={{ width: "100%" }}>
              {products?.map((item) => (
                <MobileProductCard
                  key={item.productId}
                  sx={{ mx: "auto", maxWidth: "100%" }}
                >
                  <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
                    <Avatar
                      src={API_URL + "/" + item.productImage}
                      alt={item.productName}
                      variant="rounded"
                      onClick={() =>
                        handleOpenImageModal(
                          item.productImage,
                          item.productName
                        )
                      }
                      sx={{
                        width: 80,
                        height: 80,
                        objectFit: "cover",
                        cursor: "pointer",
                      }}
                    />
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="subtitle1" fontWeight="600">
                        {item.productName}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Ürün ID: {item.productId}
                      </Typography>
                      <StatusChip status={item.productVisible} sx={{ mt: 1 }}>
                        {item.productVisible ? (
                          <ActiveIcon fontSize="small" />
                        ) : (
                          <InactiveIcon fontSize="small" />
                        )}
                        {item.productVisible ? "Aktif" : "Pasif"}
                      </StatusChip>
                    </Box>
                  </Box>

                  <Grid container spacing={2} sx={{ mb: 2 }}>
                    <Grid item xs={4}>
                      <Typography variant="caption" color="text.secondary">
                        MİLYEM
                      </Typography>
                      <Typography variant="body2" fontWeight="600">
                        {item.productGainMilyem}
                      </Typography>
                    </Grid>
                    <Grid item xs={4}>
                      <Typography variant="caption" color="text.secondary">
                        GRAM
                      </Typography>
                      <Typography variant="body2" fontWeight="600">
                        {item.productGram}
                      </Typography>
                    </Grid>
                    <Grid item xs={4}>
                      <Typography variant="caption" color="text.secondary">
                        AYAR
                      </Typography>
                      <Typography variant="body2" fontWeight="600">
                        {item.productTuningMilyem}
                      </Typography>
                    </Grid>
                  </Grid>

                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 2,
                      mt: 2,
                      pt: 2,
                      borderTop: 1,
                      borderColor: "divider",
                    }}
                  >
                    <Typography
                      variant="body1"
                      sx={{
                        fontWeight: 600,
                        color: "success.main",
                        bgcolor: "success.lighter",
                        p: 1,
                        borderRadius: 1,
                        textAlign: "center",
                        width: "100%",
                      }}
                    >
                      {new Intl.NumberFormat("tr-TR", {
                        style: "currency",
                        currency: "TRY",
                      }).format(Math.round(item.productPrice * 100) / 100)}
                    </Typography>

                    <Box
                      sx={{
                        display: "flex",
                        gap: 1,
                        justifyContent: "space-between",
                        width: "100%",
                        flexDirection: { xs: "column", sm: "row" },
                      }}
                    >
                      <Button
                        onClick={() => handleEditProduct(item)}
                        variant="outlined"
                        color="primary"
                        startIcon={<PencilIcon />}
                        sx={{ flex: 1, mb: { xs: 1, sm: 0 } }}
                        size={isMobile ? "small" : "medium"}
                      >
                        Düzenle
                      </Button>
                      <Button
                        onClick={() => handleOpenDeleteModal(item)}
                        variant="outlined"
                        color="error"
                        startIcon={<TrashIcon />}
                        sx={{ flex: 1, mb: { xs: 1, sm: 0 } }}
                        size={isMobile ? "small" : "medium"}
                      >
                        Sil
                      </Button>
                      <Button
                        onClick={() => handleOpenHideModal(item)}
                        variant="outlined"
                        color="info"
                        startIcon={
                          item.productVisible ? <InactiveIcon /> : <EyeIcon />
                        }
                        sx={{ flex: 1 }}
                        size={isMobile ? "small" : "medium"}
                      >
                        {item.productVisible ? "Gizle" : "Göster"}
                      </Button>
                    </Box>
                  </Box>
                </MobileProductCard>
              ))}
            </Box>
          ) : (
            <TableContainer
              sx={{
                maxHeight: "calc(100vh - 300px)",
                minHeight: "400px",
                width: "100%",
                overflowX: "auto",
                "&::-webkit-scrollbar": {
                  width: "8px",
                  height: "8px",
                },
                "&::-webkit-scrollbar-track": {
                  backgroundColor: "grey.100",
                },
                "&::-webkit-scrollbar-thumb": {
                  backgroundColor: "primary.light",
                  borderRadius: "4px",
                },
              }}
            >
              <Table
                stickyHeader
                sx={{
                  minWidth: { xs: 800, md: 1200 },
                  "& .MuiTableCell-root": {
                    whiteSpace: "nowrap",
                  },
                }}
              >
                <TableHead>
                  <TableRow>
                    {TABLE_HEAD.map((column) => (
                      <StyledTableCell
                        key={column.id}
                        sx={{
                          width: column.width,
                          minWidth: column.width,
                        }}
                      >
                        <Typography variant="subtitle2" fontWeight="bold">
                          {column.label}
                        </Typography>
                      </StyledTableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {products?.map((item) => (
                    <StyledTableRow key={item.productId}>
                      <StyledTableCell>
                        <Box
                          component="img"
                          src={API_URL + "/" + item.productImage}
                          alt={item.productName}
                          sx={{
                            width: "60px",
                            height: "80px",
                            objectFit: "cover",
                            borderRadius: 1,
                            cursor: "pointer",
                          }}
                          onClick={() =>
                            handleOpenImageModal(
                              item.productImage,
                              item.productName
                            )
                          }
                        />
                      </StyledTableCell>
                      <StyledTableCell>
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 0.5,
                          }}
                        >
                          <Typography variant="subtitle2" fontWeight="600">
                            {item.productName}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Ürün ID: {item.productId}
                          </Typography>
                        </Box>
                      </StyledTableCell>
                      <StyledTableCell>
                        <Typography
                          variant="body2"
                          sx={{ fontWeight: 600, color: "primary.main" }}
                        >
                          {item.productGainMilyem}
                        </Typography>
                      </StyledTableCell>
                      <StyledTableCell>
                        <Typography
                          variant="body2"
                          sx={{ fontWeight: 600, color: "primary.main" }}
                        >
                          {item.productGram}
                        </Typography>
                      </StyledTableCell>
                      <StyledTableCell>
                        <Typography
                          variant="body2"
                          sx={{ fontWeight: 600, color: "primary.main" }}
                        >
                          {item.productCaratFk?.caratName}
                        </Typography>
                      </StyledTableCell>
                      <StyledTableCell>
                        <StatusChip status={item.productVisible}>
                          {item.productVisible ? (
                            <ActiveIcon fontSize="small" />
                          ) : (
                            <InactiveIcon fontSize="small" />
                          )}
                          {item.productVisible ? "Aktif" : "Pasif"}
                        </StatusChip>
                      </StyledTableCell>
                      <StyledTableCell>
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 600,
                            color: "success.main",
                            bgcolor: "success.lighter",
                            p: 1,
                            borderRadius: 1,
                            textAlign: "center",
                          }}
                        >
                          {new Intl.NumberFormat("tr-TR", {
                            style: "currency",
                            currency: "TRY",
                          }).format(Math.round(item.productPrice * 100) / 100)}
                        </Typography>
                      </StyledTableCell>
                      <StyledTableCell>
                        <Box
                          sx={{
                            display: "flex",
                            gap: 1,
                            justifyContent: "flex-end",
                            "& .MuiIconButton-root": {
                              p: 1,
                            },
                          }}
                        >
                          <Tooltip title="Ürünü Düzenle" arrow>
                            <IconButton
                              onClick={() => handleEditProduct(item)}
                              size="small"
                              sx={{
                                color: "primary.main",
                                "&:hover": {
                                  bgcolor: "primary.lighter",
                                  transform: "scale(1.1)",
                                },
                              }}
                            >
                              <PencilIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Ürünü Sil" arrow>
                            <IconButton
                              onClick={() => handleOpenDeleteModal(item)}
                              size="small"
                              sx={{
                                color: "error.main",
                                "&:hover": {
                                  bgcolor: "error.lighter",
                                  transform: "scale(1.1)",
                                },
                              }}
                            >
                              <TrashIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip
                            title={
                              item.productVisible
                                ? "Ürünü Gizle"
                                : "Ürünü Göster"
                            }
                            arrow
                          >
                            <IconButton
                              onClick={() => handleOpenHideModal(item)}
                              size="small"
                              sx={{
                                color: "info.main",
                                "&:hover": {
                                  bgcolor: "info.lighter",
                                  transform: "scale(1.1)",
                                },
                              }}
                            >
                              {item.productVisible ? (
                                <InactiveIcon />
                              ) : (
                                <EyeIcon />
                              )}
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </StyledTableCell>
                    </StyledTableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>

        {/* Sayfalama */}
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            justifyContent: "space-between",
            alignItems: { xs: "stretch", sm: "center" },
            gap: 2,
            mt: 3,
            px: 2,
            py: 1,
            borderTop: 1,
            borderColor: "divider",
          }}
        >
          {/* Sayfa Bilgileri */}
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              alignItems: { xs: "center", sm: "baseline" },
              gap: 1,
            }}
          >
            <Typography variant="body2" color="text.secondary">
              Toplam {totalItems} ürün
            </Typography>
            <Typography
              variant="body2"
              sx={{
                px: 1.5,
                py: 0.5,
                borderRadius: 1,
                bgcolor: "primary.lighter",
                color: "primary.main",
                fontWeight: 500,
              }}
            >
              Sayfa {currentPage + 1} / {totalPages}
            </Typography>
          </Box>

          {/* Sayfalama Butonları */}
          <Box
            sx={{
              display: "flex",
              gap: 1,
              justifyContent: { xs: "center", sm: "flex-end" },
            }}
          >
            <Button
              variant="outlined"
              size="small"
              disabled={currentPage <= 0}
              onClick={() => handlePageChange(0)}
              sx={{ minWidth: 40, p: 1 }}
            >
              <Typography variant="body2">«</Typography>
            </Button>
            <Button
              variant="outlined"
              size="small"
              disabled={currentPage <= 0}
              onClick={() => handlePageChange(currentPage - 1)}
              startIcon={<NavigateBeforeIcon />}
            >
              Önceki
            </Button>

            {/* Sayfa Numaraları */}
            <Box
              sx={{
                display: "flex",
                gap: 1,
                alignItems: "center",
              }}
            >
              {[...Array(totalPages)].map((_, index) => {
                if (
                  index === 0 || // İlk sayfa
                  index === totalPages - 1 || // Son sayfa
                  Math.abs(currentPage - index) <= 1 // Aktif sayfanın yanındaki sayfalar
                ) {
                  return (
                    <Button
                      key={index}
                      variant={currentPage === index ? "contained" : "outlined"}
                      size="small"
                      onClick={() => handlePageChange(index)}
                      sx={{
                        minWidth: 40,
                        p: 1,
                        ...(currentPage === index && {
                          "&:hover": {
                            bgcolor: "primary.main",
                          },
                        }),
                      }}
                    >
                      <Typography variant="body2">{index + 1}</Typography>
                    </Button>
                  );
                } else if (
                  index === currentPage - 2 ||
                  index === currentPage + 2
                ) {
                  return (
                    <Typography
                      key={index}
                      variant="body2"
                      color="text.secondary"
                      sx={{ px: 1 }}
                    >
                      ...
                    </Typography>
                  );
                }
                return null;
              })}
            </Box>

            <Button
              variant="outlined"
              size="small"
              disabled={currentPage >= totalPages - 1}
              onClick={() => handlePageChange(currentPage + 1)}
              endIcon={<NavigateNextIcon />}
            >
              Sonraki
            </Button>
            <Button
              variant="outlined"
              size="small"
              disabled={currentPage >= totalPages - 1}
              onClick={() => handlePageChange(totalPages - 1)}
              sx={{ minWidth: 40, p: 1 }}
            >
              <Typography variant="body2">»</Typography>
            </Button>
          </Box>
        </Box>
      </CardContent>

      {/* Delete Dialog */}
      <Dialog open={openDeleteModal} onClose={handleOpenDeleteModal}>
        <DialogContent sx={{ p: 4 }}>
          <Typography variant="h6" align="center" fontWeight="bold">
            Ürünü silmek istediğinize emin misiniz?
          </Typography>
          <DialogActions sx={{ justifyContent: "center", mt: 3, gap: 2 }}>
            <Button
              onClick={handleOpenDeleteModal}
              variant="contained"
              color="error"
              sx={{ width: 100 }}
            >
              Hayır
            </Button>
            <Button
              onClick={handleYesDeleteProduct}
              variant="contained"
              color="success"
              sx={{ width: 100 }}
            >
              Evet
            </Button>
          </DialogActions>
        </DialogContent>
      </Dialog>

      {/* Hide Dialog */}
      <Dialog open={openHideModal} onClose={handleOpenHideModal}>
        <DialogContent sx={{ p: 4 }}>
          <Typography variant="h6" align="center" fontWeight="bold">
            Ürünü gizlemek istediğinize emin misiniz?
          </Typography>
          <DialogActions sx={{ justifyContent: "center", mt: 3, gap: 2 }}>
            <Button
              onClick={handleOpenHideModal}
              variant="contained"
              color="error"
              sx={{ width: 100 }}
            >
              Hayır
            </Button>
            <Button
              onClick={handleYesHideProduct}
              variant="contained"
              color="success"
              sx={{ width: 100 }}
            >
              Evet
            </Button>
          </DialogActions>
        </DialogContent>
      </Dialog>

      {/* Resim Modal'ı */}
      <Dialog
        open={openImageModal}
        onClose={() => setOpenImageModal(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogContent sx={{ p: 0, position: "relative" }}>
          <IconButton
            onClick={() => setOpenImageModal(false)}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              color: "grey.100",
              bgcolor: "rgba(0, 0, 0, 0.4)",
              "&:hover": {
                bgcolor: "rgba(0, 0, 0, 0.6)",
              },
            }}
          >
            <CloseIcon />
          </IconButton>
          <Box
            sx={{
              width: "100%",
              height: "500px",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              bgcolor: "grey.900",
            }}
          >
            <Box
              component="img"
              src={selectedImage.url}
              alt={selectedImage.name}
              sx={{
                maxWidth: "100%",
                maxHeight: "100%",
                objectFit: "contain",
              }}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2, bgcolor: "grey.900" }}>
          <Typography variant="subtitle1" sx={{ color: "grey.100" }}>
            {selectedImage.name}
          </Typography>
        </DialogActions>
      </Dialog>
    </Card>
  );
};

export default GoldProducts;
